import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { Caseload, Homeless, User, CareCoordinatorReferral } from 'types';

import EligibilityPopUp from './components/EligibilityComponents/PopUp';
import EmptyList from './components/EmptyList';
import EnrollmentProgress from './components/EnrollmentProgress';
import FAQButton from './components/FAQButton';
import ListHeader from './components/ListHeader';
import ListItem from './components/MemberCard';
import LoadingCircle from '../_shared/LoadingCircle';
import Nav from '../_shared/NavSelector';
import PopupReferral from './components/ReferralComponents/PopupReferral';
import SearchBar from './components/SearchBar';

import useDebounce from 'hooks/useDebounce';
import { isObjectEmpty } from 'util/index';

import { closePopUp, openPopUp } from 'redux/store/slices/eligibilitySlice';

import { Box, Grid, Skeleton, useMediaQuery } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { fetchActiveOrganizationCommitments } from 'api/commitments';
import { canUserSignUp, shouldHideIntakeDeployment, SignUpAvailabilityResult } from 'util/user';
import useIntakeDeploymentOptions from 'hooks/api/useIntakeDeploymentOptions';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            overflow: 'auto',
            width: '100%'
        },
        empty: {
            fontSize: '1.2rem',
            fontWeight: 600,
            marginLeft: '16px'
        },
        holderList: {
            margin: '16px 0',
            width: '96%'
        },
        listContainer: {

        },
        navContainer: {
            width: '95%'
        },
        skeletonContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            justifyContent: 'center',
            width: '98%'
        }
    })
);

type HoldersListProps = {
    caseloads: Caseload[];
    getCaseloads: () => void;
    getHomelesses: () => void;
    homelesses: Homeless[];
    incomingCareCoordinatorReferrals: CareCoordinatorReferral[] | null;
    isFetchingIncomingCareCoordinatorReferrals: boolean;
    isFetchingCaseloads: boolean;
    isFetchingHomelesses: boolean;
    layoutTitles: {
        subtitle: string;
        title: string;
    };
    fetchIncomingCareCoordinatorReferrals: (userID: number) => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
    resetCatchUpItemStatus: () => void;
    resetHomelessInfo: () => void;
    resetMemberReferrals: () => void;
    resetMessageInfo: () => void;
    resetTouchpointAnswers: () => void;
    resetUnenrollmentState: () => void;
    saveMemberToMyMembers: (caseloadID: number, memberID: number) => void;
    setLayoutTitles: (title: string, subtitle: string) => void;
    user: User;
};

const HoldersList: React.FC<HoldersListProps> = memo(
    ({
        caseloads,
        getCaseloads,
        getHomelesses,
        homelesses,
        incomingCareCoordinatorReferrals,
        isFetchingIncomingCareCoordinatorReferrals,
        isFetchingCaseloads,
        isFetchingHomelesses,
        layoutTitles,
        fetchIncomingCareCoordinatorReferrals,
        removeMemberFromMyMembers,
        resetCatchUpItemStatus,
        resetHomelessInfo,
        resetMemberReferrals,
        resetMessageInfo,
        resetTouchpointAnswers,
        resetUnenrollmentState,
        saveMemberToMyMembers,
        setLayoutTitles,
        user
    }) => {
        const classes = useStyles();
        const dispatch = useDispatch();

        ReactGA.send({ hitType: 'pageview', page: '/holders' });
        const { is_non_profit, partner } = user;
        const {
            id: user_org_id,
            name: org_name
        } = partner || '';

        const [active, setActive] = useState('My Members');
        const [userOrgID, setUserOrgID] = useState(user_org_id);
        const [referCaseload, setReferCaseload] = useState<Caseload | null>(
            null
        );
        const [referHolder, setReferHolder] = useState<Homeless | null>(null);
        const [referPopup, setReferPopup] = useState(false);
        const [state, setState] = useState({
            query: ''
        });

        const { query } = state;
        const debouncedQuery = useDebounce(query, 300);

        const empty = homelesses && homelesses.length === 0;

        const isEligibilityPopUpOpen = useSelector(
            (state: any) => state.eligibility.isPopUpOpen
        );
        const isLoading = isFetchingCaseloads || isFetchingHomelesses || isFetchingIncomingCareCoordinatorReferrals || isObjectEmpty(homelesses);
        const isSmall = useMediaQuery('(max-width: 1278px)');

        const [isFetchingCommitments, setIsFetchingCommitments] = useState(true);
        const [organizationCommitments, setOrganizationCommitments] = useState<any>(null);
        const [fetchCommitmentsError, setFetchCommitmentsError] = useState(false);
        const shouldShowSignupButton = useMemo(() => {
            return !isFetchingCommitments && (organizationCommitments?.commitments?.length === 0 || fetchCommitmentsError);
        }, [isFetchingCommitments, organizationCommitments, fetchCommitmentsError]);


        const { intakeDeploymentOptions, isFetchingIntakeDeploymentOptions, fetchIntakeDeploymentOptionsError } = useIntakeDeploymentOptions(user);
        const canSignUpMember = useMemo<SignUpAvailabilityResult>(() => {
            if (isFetchingIntakeDeploymentOptions || fetchIntakeDeploymentOptionsError) {
                return canUserSignUp(
                    user,
                    (user.partner?.deployments || []).filter(
                        (deployment) => !shouldHideIntakeDeployment(deployment)
                    )
                );
            }
            console.log('intakeDeploymentOptions', intakeDeploymentOptions);
            return canUserSignUp(user, intakeDeploymentOptions);
        }, [isFetchingIntakeDeploymentOptions, intakeDeploymentOptions, fetchIntakeDeploymentOptionsError, user, user.partner?.deployments]);

        const filterMembers = useCallback(
            (
                members: Homeless[],
                filterFunc: (item: Homeless) => boolean
            ): Homeless[] => {
                return members && members.length > 0
                    ? members
                        .filter((item) => filterFunc(item))
                        .sort((a, b) => {
                            // Move items with profile_stage 'PENDING' to the top
                            if (
                                a.profile_stage === 'PENDING' &&
                                b.profile_stage !== 'PENDING'
                            )
                                return -1;
                            if (
                                a.profile_stage !== 'PENDING' &&
                                b.profile_stage === 'PENDING'
                            )
                                return 1;
                            return 0;
                        })
                    : [];
            },
            []
        );

        const filteredCaseloadMembers = useMemo(() => {
            const filteredMembers = (
                members: Homeless[],
                caseloads: Caseload[],
            ) => {
                const filteredMembers = filterMembers(
                    members,
                    (member) =>
                        member.profile_stage !== 'EXITED' &&
                        member.profile_stage !== 'UNSUCCESSFUL_PENDING' &&
                        (
                            // Check for caseload conditions
                            (caseloads || []).some(
                                (caseload) =>
                                    caseload.homeless_id === member.id &&
                                    (caseload.status === 'active' ||
                                        caseload.status === 'pending' ||
                                        (caseload.status === 'cancelled' &&
                                            caseload.is_accepted &&
                                            caseload.is_accepted ===
                                            'referral rejected'))
                            ) ||
                            // Check for active care coordinator referrals
                            (incomingCareCoordinatorReferrals || []).some(
                                (referral) =>
                                    referral.homeless_id === member.id &&
                                    referral.is_active
                            )
                        ) 
                );

                // Sort filteredMembers array by caseload status, pending first
                filteredMembers.sort((a, b) => {
                    const statusOrder = {
                        pending: 0,
                        'referral accepted': 1,
                        'referral rejected': 2,
                        active: 3,
                        '': 99
                    } as { [key: string]: number };

                    const getProfileStageOrder = (member: Homeless) =>
                        member.profile_stage === 'PENDING' ||
                            member.profile_stage === 'MEMBERSHIP_CONFIRMED' ||
                            member.profile_stage === 'UNSUCCESSFUL_PENDING'
                            ? -1
                            : 1;

                    const profileStageOrder =
                        getProfileStageOrder(a) - getProfileStageOrder(b);

                    if (profileStageOrder !== 0) {
                        return profileStageOrder;
                    }

                    const getStatusOrder = (memberId: number) => {
                        const caseload = (caseloads || []).find(
                            (caseload) => caseload.homeless_id === memberId
                        );

                        return caseload
                            ? statusOrder[
                            caseload.is_accepted
                                ? caseload.is_accepted
                                : caseload.status.toLowerCase()
                            ]
                            : statusOrder[''];
                    };

                    const aStatusOrder = getStatusOrder(a.id);
                    const bStatusOrder = getStatusOrder(b.id);

                    return aStatusOrder - bStatusOrder;
                });

                return filteredMembers;
            };

            return filteredMembers;
        }, [filterMembers, incomingCareCoordinatorReferrals]);

        const renderMembers = useCallback(
            (memberList: Homeless[]) => {
                if (memberList === null) {
                    return <LoadingCircle />;
                }

                if (memberList !== null && memberList.length === 0 && !empty) {
                    return (
                        <p className={classes.empty}>
                            No Members match your search
                        </p>
                    );
                }

                if (memberList !== null && memberList.length > 0) {
                    return memberList.map((h) => {
                        const memberCaseload = caseloads?.filter(
                            (caseload) => caseload.homeless_id === h.id
                        )[0];

                        const memberCareCoordinatorReferrals = incomingCareCoordinatorReferrals?.filter(
                            (referral) => referral.homeless_id === h.id
                        ) || null;

                        return (
                            <Grid
                                key={`member-${h.id}`}
                                xs={12}
                                sm={6}
                                lg={4}
                                sx={{
                                    minHeight: '240px',
                                    maxHeight: '240px',
                                    padding: isSmall
                                        ? '8px !important'
                                        : '20px !important'
                                }}
                                item
                            >
                                <ListItem
                                    caseload={memberCaseload}
                                    holder={h}
                                    memberCareCoordinatorReferrals={memberCareCoordinatorReferrals}
                                    setReferCaseload={setReferCaseload}
                                    setReferHolder={setReferHolder}
                                    setReferPopup={setReferPopup}
                                />
                            </Grid>
                        );
                    });
                }
            },
            [classes.empty, caseloads, empty, isSmall, incomingCareCoordinatorReferrals]
        );

        const SkeletonLayout = () => {
            return (
                <>
                    <Box sx={{ padding: 2, width: '96%' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Skeleton
                                    variant="rounded"
                                    height={64}
                                    animation="wave"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Skeleton
                                    variant="rounded"
                                    height={48}
                                    width="100%"
                                    animation="wave"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Skeleton
                                    variant="rounded"
                                    height={48}
                                    width="100%"
                                    animation="wave"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Skeleton
                                    variant="rounded"
                                    height={48}
                                    width="100%"
                                    animation="wave"
                                />
                            </Grid>
                            {[...Array(9)].map((_, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Skeleton
                                        variant="rounded"
                                        height={240}
                                        animation="wave"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </>
            );
        };

        const myMembers = filteredCaseloadMembers(homelesses, caseloads);

        const searchMembersByQuery = useMemo(() => {
            let filteredMembers = homelesses;

            if (debouncedQuery.length > 0) {
                let queryCharacters = debouncedQuery
                    .split('')
                    .map((c) => {
                        return c === ' '
                            ? '\\s'
                            : `[${c.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`;
                    })
                    .join('(\\w|\\s)*');

                filteredMembers = homelesses.filter((h) => {
                    const nameRegex = new RegExp(queryCharacters, 'gi');
                    const fullName =
                        `${h.first_name || ''} ${h.last_name || ''}`.trim();

                    return (
                        (h.organization_id === user_org_id ||
                            myMembers.includes(h) ||
                            (caseloads || []).some(
                                (caseload) =>
                                    caseload.homeless_id === h.id &&
                                    caseload.status === 'active'
                            )) &&
                        (h.name?.match(nameRegex) ||
                            h.first_name?.match(nameRegex) ||
                            h.last_name?.match(nameRegex) ||
                            fullName.match(nameRegex))
                    );
                });
            }

            return renderMembers(filteredMembers);
        }, [
            debouncedQuery,
            homelesses,
            myMembers,
            renderMembers,
            user_org_id,
            caseloads
        ]);

        const setQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setState({ query: value });
        };

        const exitedMembers = !isObjectEmpty(homelesses)
            ? [
                ...homelesses.filter(
                    (item) =>
                        (item.organization_id === user_org_id ||
                            (caseloads || []).some(
                                (caseload) =>
                                    caseload.homeless_id === item.id &&
                                    caseload.status === 'active'
                            )) &&
                        item.profile_stage === 'UNSUCCESSFUL_PENDING'
                ),
                ...homelesses.filter(
                    (item) =>
                        (item.organization_id === user_org_id ||
                            (caseloads || []).some(
                                (caseload) =>
                                    caseload.homeless_id === item.id &&
                                    caseload.status === 'active'
                            )) &&
                        item.profile_stage === 'EXITED'
                )
            ]
            : [];

        const orgMembers = filterMembers(
            homelesses,
            (item) =>
                item.organization_id === user_org_id &&
                item.profile_stage !== 'EXITED' &&
                item.profile_stage !== 'UNSUCCESSFUL_PENDING' &&
                !myMembers.some((m) => m.id === item.id)
        );

        const pendingMembers = filterMembers(
            homelesses,
            (item) =>
                item.organization_id === user_org_id &&
                item.profile_stage === 'PENDING'
        );

        const navItems = useMemo(() => {
            return [
                myMembers.length || empty ? 'My Members' : null,
                (org_name && orgMembers.length) || empty
                    ? `${org_name} Members`
                    : null,
                pendingMembers.length || empty ? 'Pending Members' : null,
                exitedMembers.length || empty ? `Unenrolled Members` : null
            ];
        }, [
            empty,
            exitedMembers.length,
            myMembers.length,
            orgMembers.length,
            org_name,
            pendingMembers.length
        ]);

        useEffect(() => {
            if (user.partner && user.is_non_profit && !userOrgID) {
                setUserOrgID(user.partner.id);
            } else {
                setUserOrgID(user_org_id);
            }
        }, [user, userOrgID, user_org_id]);

        useEffect(() => {
            if (layoutTitles.subtitle !== 'Client List') {
                setLayoutTitles('', 'Client List');
            }

            resetCatchUpItemStatus();
            resetHomelessInfo();
            resetMessageInfo();
            resetMemberReferrals();
            resetTouchpointAnswers();
            resetUnenrollmentState();
        }, [
            layoutTitles.subtitle,
            resetCatchUpItemStatus,
            resetHomelessInfo,
            resetMemberReferrals,
            resetMessageInfo,
            resetTouchpointAnswers,
            resetUnenrollmentState,
            setLayoutTitles
        ]);

        useEffect(() => {
            if (!isFetchingCaseloads && caseloads === null && user?.id) {
                getCaseloads();
            }
        }, [caseloads, getCaseloads, isFetchingCaseloads, user?.id]);

        useEffect(() => {
            if (user?.id && user.is_care_coordinator) {
                fetchIncomingCareCoordinatorReferrals(user.id);
            }
        }, [user?.id, user?.is_care_coordinator, fetchIncomingCareCoordinatorReferrals]);

        useEffect(() => {
            if (
                !isFetchingHomelesses &&
                isObjectEmpty(homelesses) &&
                userOrgID &&
                is_non_profit
            ) {
                getHomelesses();
            }
        }, [
            getHomelesses,
            homelesses,
            isFetchingHomelesses,
            is_non_profit,
            userOrgID
        ]);

        useEffect(() => {
            const updateMembers = async () => getHomelesses();

            if (
                layoutTitles.subtitle !== 'Client List' &&
                layoutTitles.subtitle !== 'Member Intake' &&
                layoutTitles.subtitle !== 'Settings' &&
                layoutTitles.subtitle! === 'Your Referrals' &&
                !isObjectEmpty(homelesses)
            ) {
                const memberHandle = layoutTitles.subtitle
                    .split('. ')[1]
                    .replace(/[()]/g, '');

                if (!isObjectEmpty(homelesses)) {
                    const member = homelesses.find(
                        (h) => h.handle === memberHandle
                    );

                    if (!member) {
                        updateMembers();
                    }
                }
            }
        }, [getHomelesses, homelesses, layoutTitles.subtitle, userOrgID]);

        useEffect(() => {
            const firstNavItem = navItems.find((item) => item !== null);

            if (firstNavItem) {
                setActive(firstNavItem);
            }
        }, [navItems]);

        useEffect(() => {
            const fetchCommitmentsData = async () => {
                if (!user?.id) return;
                setIsFetchingCommitments(true);
                setFetchCommitmentsError(false);
                try {
                    const commitments = await fetchActiveOrganizationCommitments(user.partner?.id, user.id);
                    setOrganizationCommitments(commitments);
                } catch (error) {
                    console.error('Error fetching organization commitments:', error);
                    setFetchCommitmentsError(true);
                } finally {
                    setIsFetchingCommitments(false);
                }
            };

            fetchCommitmentsData();
        }, [user?.id, user.partner?.id]);

        return (
            <div className={classes.root}>
                <Helmet title="Member List" />
                <ListHeader 
                    setEligibilityPopUp={() => dispatch(openPopUp())}
                    shouldShowSignupButton={shouldShowSignupButton}
                    shouldDisableSignupButton={!canSignUpMember.canSignUp}
                />
                {!shouldShowSignupButton && (
                <EnrollmentProgress
                    setEligibilityPopUp={() => dispatch(openPopUp())}
                    isLoading={isFetchingCommitments}
                    activeCommitments={organizationCommitments} 
                    canSignUpMember={canSignUpMember}
                />
                )}
                {isLoading ? (
                    <SkeletonLayout />
                ) : (
                    <>
                        <SearchBar
                            empty={empty}
                            query={query}
                            setQuery={setQuery}
                            setState={setState}
                        />
                        <div className={classes.navContainer}>
                            {!debouncedQuery.length ? (
                                <Nav
                                    active={active}
                                    setActive={setActive}
                                    empty={empty}
                                    navItems={navItems}
                                    style={{
                                        justifyContent: 'flex-start',
                                        padding: '20px 0',
                                        margin: '0 8px'
                                    }}
                                />
                            ) : (
                                <h2>Results:</h2>
                            )}
                        </div>
                        <Grid container className={classes.holderList}>
                            {empty && <EmptyList />}
                            {debouncedQuery.length > 0 ? (
                                <>{searchMembersByQuery}</>
                            ) : (
                                <>
                                    {myMembers &&
                                        active === 'My Members' &&
                                        renderMembers(myMembers)}
                                    {orgMembers &&
                                        active === `${org_name} Members` &&
                                        renderMembers(orgMembers)}
                                    {exitedMembers &&
                                        active === 'Unenrolled Members' &&
                                        renderMembers(exitedMembers)}
                                    {pendingMembers &&
                                        active === 'Pending Members' &&
                                        renderMembers(pendingMembers)}
                                </>
                            )}
                            <FAQButton
                                isFetchingHomelesses={isFetchingHomelesses}
                            />
                        </Grid>
                    </>
                )}
                {referPopup && (
                    <PopupReferral
                        handleCloseClick={() => setReferPopup(false)}
                        referCaseload={referCaseload!}
                        referHolder={referHolder!}
                        removeMemberFromMyMembers={removeMemberFromMyMembers}
                        saveMemberToMyMembers={saveMemberToMyMembers}
                    />
                )}
                {isEligibilityPopUpOpen && (
                    <EligibilityPopUp
                        handleCloseClick={() => dispatch(closePopUp())}
                        user={user}
                    />
                )}
            </div>
        );
    }
);

export default HoldersList;